import { MAX_FILE_BYTES } from './hrModel.js';

export const MAX_REP_BYTES = 1024 * 1024;
const relevant = (name) => /\.(qrs|rep)$/i.test(name);

// Directory identity is part of the key: never combine similarly named studies from different folders.
export function groupStudies(selection) {
  const folders = new Map();
  for (const item of selection) {
    const file = item.file || item;
    if (!relevant(file.name)) continue;
    const path = (item.relativePath || file.webkitRelativePath || file.name).replaceAll('\\', '/');
    const folder = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
    if (!folders.has(folder)) folders.set(folder, new Map());
    const index = folders.get(folder);
    const name = file.name.toLowerCase();
    if (!index.has(name)) index.set(name, []);
    index.get(name).push(file);
  }
  const studies = [];
  for (const [folder, index] of folders) {
    const stems = new Set(
      [...index.keys()].filter((n) => n.endsWith('.rep')).map((n) => n.slice(0, -4))
    );
    for (const name of index.keys()) {
      if (!name.endsWith('.qrs')) continue;
      const stem = name.slice(0, -4);
      const parent = stem.slice(0, -1);
      // A REP is a strong anchor; otherwise an unsuffixed QRS anchors its a/b partners.
      if (!stems.has(stem) && /[ab]$/.test(stem)) {
        const sibling = `${parent}${stem.endsWith('a') ? 'b' : 'a'}.qrs`;
        if (index.has(`${parent}.rep`) || index.has(`${parent}.qrs`) || index.has(sibling)) {
          stems.add(parent);
          continue;
        }
      }
      stems.add(stem);
    }
    for (const stem of stems) {
      const expected = {
        rep: `${stem}.rep`,
        main: `${stem}.qrs`,
        a: `${stem}a.qrs`,
        b: `${stem}b.qrs`,
      };
      const files = {};
      const missing = [];
      const duplicate = [];
      const oversized = [];
      for (const [role, name] of Object.entries(expected)) {
        const matches = index.get(name) || [];
        if (!matches.length && (role === 'rep' || role === 'main'))
          missing.push(role === 'main' ? '.qrs' : role === 'rep' ? '.REP' : `${role}.qrs`);
        if (matches.length > 1) duplicate.push(name);
        files[role] = matches[0] || null;
        if (matches[0]?.size > (role === 'rep' ? MAX_REP_BYTES : MAX_FILE_BYTES))
          oversized.push(name);
      }
      const anchor = files.rep || files.main;
      const display = anchor ? anchor.name.slice(0, -4) : stem;
      const problems = [];
      if (missing.length) problems.push(`Missing ${missing.join(', ')}`);
      if (duplicate.length) problems.push(`Duplicate files: ${duplicate.join(', ')}`);
      if (oversized.length) problems.push('File exceeds size limit');
      studies.push({
        id: `${folder}\u0000${stem}`,
        stem: display,
        folder,
        ...files,
        ready: problems.length === 0,
        status:
          problems.join('. ') ||
          `Available: REP + primary QRS${files.a || files.b ? ' + optional ' + [files.a && 'A', files.b && 'B'].filter(Boolean).join('/') : ''}`,
      });
    }
  }
  return studies.sort(
    (a, b) =>
      a.stem.localeCompare(b.stem, undefined, { numeric: true }) || a.folder.localeCompare(b.folder)
  );
}

export function filterStudies(studies, query) {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return studies.filter((s) =>
    tokens.every((token) =>
      `${s.studyId || ''} ${s.stem} ${s.folder}`.toLowerCase().includes(token)
    )
  );
}

// Capture DataTransfer entries before awaiting; directory readers can return many batches.
export async function collectStudyDrop(dataTransfer, isCancelled = () => false) {
  const items = Array.from(dataTransfer.items || []).filter((item) => item.kind === 'file');
  const roots = items.map((item) => {
    const getter = item.getAsEntry || item.webkitGetAsEntry;
    return { entry: getter ? getter.call(item) : null, file: item.getAsFile() };
  });
  const fallback = Array.from(dataTransfer.files || []);
  const found = [];
  let visited = 0;
  const visit = async (entry, parent = '') => {
    if (isCancelled()) throw new Error('Folder scan cancelled.');
    visited += 1;
    if (visited > 200000)
      throw new Error('Folder has over 200,000 entries. Choose a smaller study folder.');
    const relativePath = parent ? `${parent}/${entry.name}` : entry.name;
    if (entry.isDirectory) {
      const reader = entry.createReader();
      let batch;
      do {
        batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        for (const child of batch) await visit(child, relativePath);
      } while (batch.length);
    } else if (entry.isFile && relevant(entry.name)) {
      const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
      found.push({ file, relativePath });
    }
  };
  if (!roots.length)
    return fallback.map((file) => ({ file, relativePath: file.webkitRelativePath || file.name }));
  for (const root of roots) {
    if (root.entry) await visit(root.entry);
    else if (root.file)
      found.push({ file: root.file, relativePath: root.file.webkitRelativePath || root.file.name });
    else throw new Error('Folder dropping is unavailable. Use Select folder instead.');
  }
  return found;
}

// Handle-only scan: no getFile(), arrayBuffer(), text(), or stream() during discovery.
export async function scanStudyDirectory(directory, isCancelled = () => false) {
  const entries = [];
  let visited = 0;
  const walk = async (handle, parent) => {
    for await (const [name, child] of handle.entries()) {
      if (isCancelled()) throw new Error('Folder scan cancelled.');
      visited += 1;
      if (visited > 200000)
        throw new Error('Folder has over 200,000 entries. Choose a smaller study folder.');
      const relativePath = `${parent}/${name}`;
      if (child.kind === 'directory') await walk(child, relativePath);
      else if (child.kind === 'file' && relevant(name)) {
        entries.push({ file: { name, handle: child }, relativePath });
      }
    }
  };
  await walk(directory, directory.name);
  return groupStudies(entries);
}

// Called only from Generate PDF. Re-resolve the selected files and validate their sizes.
export async function openStudyFiles(study, isCancelled = () => false) {
  if (!study?.ready) throw new Error('Choose a study with a REP and primary QRS file.');
  const opened = { ...study };
  await Promise.all(
    ['rep', 'main', 'a', 'b'].map(async (role) => {
      if (isCancelled()) throw new Error('Generation cancelled.');
      const reference = study[role];
      if (!reference && (role === 'a' || role === 'b')) {
        opened[role] = null;
        return;
      }
      if (!reference) throw new Error('REP and primary QRS files are required.');
      const file = reference.handle ? await reference.handle.getFile() : reference;
      if (isCancelled()) throw new Error('Generation cancelled.');
      if (file.name.toLowerCase() !== reference.name.toLowerCase())
        throw new Error('Study files changed. Select the folder again.');
      if (file.size > (role === 'rep' ? MAX_REP_BYTES : MAX_FILE_BYTES))
        throw new Error(`${file.name} exceeds the file size limit.`);
      opened[role] = file;
    })
  );
  return opened;
}
